import type { PageVisitScope } from './pageVisits'

export const COMMAND_RECENT_STORAGE_KEY = 'erp-command-recent-records'
export const MAX_COMMAND_RECENT_RECORDS = 10

export type CommandRecordType = 'products' | 'customers' | 'suppliers' | 'invoices' | 'purchases' | 'expenses'
export type CommandRecentRecord = { type: CommandRecordType; id: string; visitedAt: number }

const RECORD_TYPES = new Set<CommandRecordType>(['products', 'customers', 'suppliers', 'invoices', 'purchases', 'expenses'])

export function commandRecentStorageKey(scope: PageVisitScope): string {
  return `${COMMAND_RECENT_STORAGE_KEY}:${encodeURIComponent(scope.tenant)}:${encodeURIComponent(String(scope.userId))}`
}

export function readCommandRecentRecords(scope: PageVisitScope | null, limit = 5): CommandRecentRecord[] {
  if (!scope || typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(commandRecentStorageKey(scope))
    const parsed = raw ? JSON.parse(raw) as unknown : []
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((item): item is CommandRecentRecord => Boolean(
        item && typeof item === 'object' &&
        RECORD_TYPES.has((item as CommandRecentRecord).type) &&
        /^\d+$/.test(String((item as CommandRecentRecord).id)) &&
        Number((item as CommandRecentRecord).id) > 0 &&
        Number.isFinite((item as CommandRecentRecord).visitedAt),
      ))
      .sort((a, b) => b.visitedAt - a.visitedAt)
      .slice(0, Math.max(0, Math.min(limit, MAX_COMMAND_RECENT_RECORDS)))
  } catch {
    return []
  }
}

export function rememberCommandRecord(
  scope: PageVisitScope | null,
  type: CommandRecordType,
  id: string | number,
  now = Date.now(),
): CommandRecentRecord[] {
  if (!scope || typeof localStorage === 'undefined') return []
  const normalizedId = String(id)
  if (!/^\d+$/.test(normalizedId) || Number(normalizedId) <= 0 || !RECORD_TYPES.has(type)) return readCommandRecentRecords(scope)

  const next: CommandRecentRecord[] = [
    { type, id: normalizedId, visitedAt: now },
    ...readCommandRecentRecords(scope, MAX_COMMAND_RECENT_RECORDS).filter((item) => !(item.type === type && item.id === normalizedId)),
  ].slice(0, MAX_COMMAND_RECENT_RECORDS)
  try {
    localStorage.setItem(commandRecentStorageKey(scope), JSON.stringify(next))
  } catch {
    /* private mode or storage quota */
  }
  return next
}
